import React, { useEffect, useState } from "react";
import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
} from "downshift";
import ClickAwayListener from "react-click-away-listener";
import classNames from "classnames";
import { addOrRemoveItemInArray } from "../Utils/addOrRemoveItemInArray";
import { PartialsBackgroundVariants } from "./PartialsBackgroundVariants";
import { StreamLineIcon } from "../../SvgIcon/StreamLineIcon";

interface ComboboxPropsBase<T> {
  items: T[];
  onInputValueChange: (item: T, inputValue: string | undefined) => boolean;
  renderItemInList: (item: T) => string | React.ReactChild;
  itemToString: (item: T | null) => string;
  label: string | React.ReactChild;
  placeholder: string;
  multipleSelectPlaceholder?: string;
  backgroundVariant?: PartialsBackgroundVariants;
  className?: string;
  prefixIcon?: React.ReactChild;
  postfixIcon?: React.ReactChild;
  disabled?: boolean;
  checkBoxValueCallback?: (selectedItems: T[], itemInCombobox: T) => boolean;
  onInputValueListModifier?: (items: T[], inputValue?: string) => T[];
}

interface ComboboxPropsSingle<T> extends ComboboxPropsBase<T> {
  currentItem: T | null;
  allowMultipleSelect?: false;
  onSelectionChangeArray?: (item: T[] | null) => void;
  onSelectionChange: (item: T | null) => void;
}

interface ComboboxPropsMultiple<T> extends ComboboxPropsBase<T> {
  currentItem: T[] | null;
  allowMultipleSelect: true;
  onSelectionChangeArray: (items: T[] | null) => void;
  onSelectionChange?: (item: T | null) => void;
}

type ComboboxProps<T> = ComboboxPropsSingle<T> | ComboboxPropsMultiple<T>;

export const Combobox = <T extends {}>(props: ComboboxProps<T>) => {
  const {
    items,
    label,
    className,
    placeholder,
    multipleSelectPlaceholder,
    prefixIcon,
    postfixIcon,
    currentItem,
    onInputValueChange,
    renderItemInList,
    itemToString,
    onSelectionChange,
    onSelectionChangeArray,
    checkBoxValueCallback,
    allowMultipleSelect = false,
    backgroundVariant = "light",
    disabled = false,
    onInputValueListModifier = (items, _) => items,
  } = props;

  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [inputItems, setInputItems] = useState(items);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const comboBoxOptions = {
    items: inputItems,
    itemToString: (item: T | null) => itemToString(item),
    onInputValueChange: (stateChange: UseComboboxStateChange<T>) => {
      const { inputValue } = stateChange;
      const filteredItems = items.filter((item) =>
        onInputValueChange(item, inputValue)
      );

      setInputItems(onInputValueListModifier(filteredItems, inputValue));
    },
    onSelectedItemChange: (stateChange: UseComboboxStateChange<T>) => {
      const { selectedItem } = stateChange;

      selectedItem && onSelectionChange && onSelectionChange(selectedItem);
    },
    ...(allowMultipleSelect && {
      selectedItem: null,
      stateReducer: (
        state: UseComboboxState<T>,
        actionAndChanges: UseComboboxStateChangeOptions<T>
      ) => {
        const { changes, type } = actionAndChanges;
        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            return {
              ...changes,
              isOpen: true,
              highlightedIndex: state.highlightedIndex,
              inputValue: "",
            };
          case useCombobox.stateChangeTypes.InputBlur:
            return {
              ...changes,
              inputValue: "",
            };
          default:
            return changes;
        }
      },
      onSelectedItemChange: (stateChange: UseComboboxStateChange<T>) => {
        const { selectedItem } = stateChange;
        if (!selectedItem) {
          return;
        }
        setSelectedItems(addOrRemoveItemInArray(selectedItems, selectedItem));
      },
    }),
  };

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    reset,
    inputValue,
    openMenu,
    setInputValue,
  } = useCombobox(comboBoxOptions);

  const onClear = () => {
    reset();
    openMenu();
    onSelectionChange && onSelectionChange(null);
    allowMultipleSelect && setSelectedItems([]);
  };

  useEffect(() => {
    setInputItems(items);
  }, [items]);

  useEffect(() => {
    if (!currentItem) {
      setInputValue("");
    }

    if (!allowMultipleSelect) {
      setInputValue(itemToString(currentItem as T));
    }

    if (allowMultipleSelect && currentItem) {
      setSelectedItems(currentItem as T[]);
    }
  }, [currentItem]);

  useEffect(() => {
    if (allowMultipleSelect) {
      selectedItems &&
        onSelectionChangeArray &&
        onSelectionChangeArray(selectedItems);
    }
  }, [selectedItems]);
  const renderItems = (item: T, index: number) => (
    <li
      key={`${item}${index}`}
      className={classNames("combobox__option", {
        "combobox__option--highlighted": highlightedIndex === index,
      })}
      {...getItemProps({ item, index })}
    >
      {allowMultipleSelect && (
        <input
          type="checkbox"
          checked={
            checkBoxValueCallback
              ? checkBoxValueCallback(selectedItems, item)
              : selectedItems.includes(item)
          }
          value={itemToString(item)}
          onChange={() => null}
          className="combobox__optionCheckbox"
        />
      )}
      {renderItemInList(item)}
    </li>
  );

  const renderControlButtons = () =>
    !!inputValue ? (
      <button
        className="btn combobox__button"
        tabIndex={-1}
        onClick={onClear}
        aria-label="clear selection"
      >
        <StreamLineIcon iconName="s-close" />
      </button>
    ) : (
      <button
        {...getToggleButtonProps({ disabled })}
        className="btn combobox__button"
        type="button"
        aria-label="toggle menu"
      >
        {!postfixIcon &&
          (isOpen ? (
            <StreamLineIcon iconName="s-arrow-up" />
          ) : (
            <StreamLineIcon iconName="s-arrow-down" />
          ))}
        {postfixIcon}
      </button>
    );

  const isComboboxFocused =
    !!inputValue ||
    isInputFocused ||
    isOpen ||
    (allowMultipleSelect && selectedItems.length > 0);

  const placeholderText =
    allowMultipleSelect && selectedItems.length > 0
      ? multipleSelectPlaceholder ?? `${selectedItems.length} elements selected`
      : `${placeholder}`;

  return (
    <ClickAwayListener onClickAway={() => setIsInputFocused(false)}>
      <div
        className={classNames(
          "combobox",
          disabled && "combobox--disabled",
          className
        )}
      >
        <label
          className={classNames("fs-5 combobox__label", {
            "combobox__label--focus": isComboboxFocused,
            "combobox__label--prefixIcon": prefixIcon,
          })}
          {...getLabelProps()}
        >
          {label}
        </label>
        <div className="combobox__inner" {...getComboboxProps()}>
          {prefixIcon}
          <input
            className={classNames("fs-5 combobox__input", {
              "combobox__input--prefixIcon": prefixIcon,
              "combobox__input--light": backgroundVariant === "light",
              "combobox__input--blueLight": backgroundVariant === "blue-light",
            })}
            placeholder={
              isInputFocused || !!inputValue || selectedItems.length > 0
                ? placeholderText
                : ""
            }
            {...getInputProps({
              onFocus: () => setIsInputFocused(true),
              disabled,
            })}
          />
          {renderControlButtons()}
        </div>
        <ul className="combobox__optionsList" {...getMenuProps()}>
          {isOpen && inputItems.map(renderItems)}
        </ul>
      </div>
    </ClickAwayListener>
  );
};
